/**
 * Reanimated stub for PWA — identity animations with reactive `.value` so
 * intro/dock styles (e.g. MainScreen auth chips) actually update on web.
 * Native package / Metro path unchanged.
 */
import React from 'react';
import { FlatList, Image, ScrollView, Text, View } from 'react-native';

export type SharedValue<T> = { value: T };

export type AnimatedStyle<T = Record<string, unknown>> = T;

export type ScrollHandlerProcessed = (event: unknown) => void;

export type EntryExitAnimationFunction = (values: Record<string, unknown>) => {
  animations?: Record<string, unknown>;
  initialValues?: Record<string, unknown>;
  callback?: (finished: boolean) => void;
};

export type EntryOrExitLayoutType = EntryExitAnimationFunction | object;

export function isSharedValue(value: unknown): value is SharedValue<unknown> {
  return typeof value === 'object' && value != null && 'value' in value;
}

/**
 * Mutable shared value with coalesced React re-renders so `useAnimatedStyle`
 * updates (dock, journey selection, etc.) without "Too many re-renders".
 * Tiny numeric churn (layout jitter) does not bump.
 */
export function useSharedValue<T>(initial: T): SharedValue<T> {
  const store = React.useRef({ value: initial });
  const [, bump] = React.useReducer((n: number) => n + 1, 0);
  const bump_scheduled = React.useRef(false);
  return React.useMemo(() => {
    const sv = {
      get value() {
        return store.current.value;
      },
      set value(next: T) {
        const prev = store.current.value;
        if (Object.is(prev, next)) {
          return;
        }
        if (
          typeof prev === 'number' &&
          typeof next === 'number' &&
          Number.isFinite(prev) &&
          Number.isFinite(next) &&
          Math.abs(prev - next) < 0.51
        ) {
          store.current.value = next;
          return;
        }
        store.current.value = next;
        if (bump_scheduled.current) {
          return;
        }
        bump_scheduled.current = true;
        queueMicrotask(() => {
          bump_scheduled.current = false;
          bump();
        });
      },
    };
    return sv;
  }, []);
}

export function useAnimatedStyle(updater: () => Record<string, unknown>, _deps?: unknown[]) {
  try {
    return updater() ?? {};
  } catch {
    return {};
  }
}

export function useAnimatedProps(updater: () => Record<string, unknown>, _deps?: unknown[]) {
  try {
    return updater() ?? {};
  } catch {
    return {};
  }
}

export function useDerivedValue<T>(updater: () => T, deps?: unknown[]): SharedValue<T> {
  let initial: T;
  try {
    initial = updater();
  } catch {
    initial = undefined as T;
  }
  const sv = useSharedValue(initial);
  React.useEffect(() => {
    try {
      sv.value = updater();
    } catch {
      // ignore
    }
    // Intentionally re-run when deps change; omit updater identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? []);
  return sv;
}

export function useAnimatedScrollHandler(
  handlers:
    | ((event: { contentOffset: { y: number; x: number } }) => void)
    | {
        onScroll?: (event: { contentOffset: { y: number; x: number } }) => void;
        onBeginDrag?: (event: unknown) => void;
        onEndDrag?: (event: unknown) => void;
        onMomentumEnd?: (event: unknown) => void;
      },
  _deps?: unknown[],
): ScrollHandlerProcessed {
  return (event: unknown) => {
    const e = event as {
      nativeEvent?: { contentOffset?: { y?: number; x?: number } };
      contentOffset?: { y?: number; x?: number };
    };
    const contentOffset = e?.nativeEvent?.contentOffset ?? e?.contentOffset ?? { y: 0, x: 0 };
    const payload = {
      contentOffset: {
        y: contentOffset.y ?? 0,
        x: contentOffset.x ?? 0,
      },
    };
    if (typeof handlers === 'function') {
      handlers(payload);
      return;
    }
    handlers.onScroll?.(payload);
  };
}

export function useAnimatedReaction<T>(
  prepare: () => T,
  react: (current: T, previous: T | null) => void,
  _deps?: unknown[],
): void {
  const prev = React.useRef<T | null>(null);
  // Run after each commit (Reanimated reacts continuously). Only invoke
  // `react` when `prepare()` changes so we do not loop on derived writes.
  React.useEffect(() => {
    try {
      const current = prepare();
      if (Object.is(current, prev.current)) {
        return;
      }
      react(current, prev.current);
      prev.current = current;
    } catch {
      // ignore worklet errors on web
    }
  });
}

export function useReducedMotion() {
  return true;
}

export function withTiming<T>(value: T, _config?: unknown, callback?: (finished: boolean) => void): T {
  if (typeof callback === 'function') {
    queueMicrotask(() => callback(true));
  }
  return value;
}

export function withSpring<T>(value: T, _config?: unknown, callback?: (finished: boolean) => void): T {
  if (typeof callback === 'function') {
    queueMicrotask(() => callback(true));
  }
  return value;
}

export function withDelay<T>(_ms: number, value: T): T {
  return value;
}

export function withSequence<T>(...values: T[]): T {
  return values[values.length - 1] as T;
}

export function withRepeat<T>(value: T, _times?: number, _reverse?: boolean): T {
  return value;
}

export function cancelAnimation(_value: SharedValue<unknown>): void {}

export function interpolate(
  value: number,
  input: number[],
  output: number[],
  _extrapolation?: unknown,
): number {
  if (!input.length || !output.length) {
    return value;
  }
  if (value <= input[0]!) {
    return output[0]!;
  }
  const last = input.length - 1;
  if (value >= input[last]!) {
    return output[Math.min(last, output.length - 1)]!;
  }
  for (let i = 0; i < last; i++) {
    const i0 = input[i]!;
    const i1 = input[i + 1]!;
    if (value >= i0 && value <= i1) {
      const t = (value - i0) / (i1 - i0 || 1);
      const o0 = output[i] ?? output[0]!;
      const o1 = output[i + 1] ?? o0;
      return o0 + (o1 - o0) * t;
    }
  }
  return value;
}

export function interpolateColor(value: number, input: number[], output: string[]): string {
  const idx = Math.max(0, Math.min(output.length - 1, Math.round(value)));
  return output[idx] ?? '#000';
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export const Extrapolation = {
  CLAMP: 'clamp',
  EXTEND: 'extend',
  IDENTITY: 'identity',
} as const;

export const Easing = {
  linear: (t: number) => t,
  ease: (t: number) => t,
  quad: (t: number) => t,
  cubic: (t: number) => t,
  poly: () => (t: number) => t,
  sin: (t: number) => t,
  circle: (t: number) => t,
  exp: (t: number) => t,
  elastic: () => (t: number) => t,
  back: () => (t: number) => t,
  bounce: (t: number) => t,
  bezier: () => (t: number) => t,
  in: (fn: (t: number) => number) => fn,
  out: (fn: (t: number) => number) => fn,
  inOut: (fn: (t: number) => number) => fn,
};

export const runOnJS =
  <T extends (...args: never[]) => unknown>(fn: T) =>
  (...args: Parameters<T>) =>
    fn(...args);

export const runOnUI =
  <T extends (...args: never[]) => unknown>(fn: T) =>
  (...args: Parameters<T>) =>
    fn(...args);

/**
 * Identity layout/entering builders. Real Reanimated returns a new builder per
 * `.duration()` / `.easing()` / … call; we return a Proxy so every chain method
 * exists and returns the same builder (avoids crashes like JourneyScreen's
 * `FadeIn.duration(...).easing is not a function` when the stub lags the API).
 */
function makeEntering(name: string) {
  const factory = function enteringFactory() {
    return {
      animations: {},
      initialValues: {},
    };
  };
  return new Proxy(factory, {
    get(target, prop, receiver) {
      if (prop === '__name') {
        return name;
      }
      if (prop === 'build') {
        return () => factory;
      }
      if (
        prop === 'prototype' ||
        prop === 'name' ||
        prop === 'length' ||
        prop === 'apply' ||
        prop === 'call' ||
        prop === 'bind' ||
        prop === Symbol.toStringTag ||
        typeof prop === 'symbol'
      ) {
        return Reflect.get(target, prop, receiver);
      }
      // duration / delay / easing / springify / damping / …
      return (..._args: unknown[]) => receiver;
    },
  });
}

export const FadeIn = makeEntering('FadeIn');
export const FadeInDown = makeEntering('FadeInDown');
export const FadeInUp = makeEntering('FadeInUp');
export const FadeOut = makeEntering('FadeOut');
export const FadeOutDown = makeEntering('FadeOutDown');
export const FadeOutUp = makeEntering('FadeOutUp');
export const SlideInRight = makeEntering('SlideInRight');
export const SlideInLeft = makeEntering('SlideInLeft');
export const SlideInDown = makeEntering('SlideInDown');
export const SlideInUp = makeEntering('SlideInUp');
export const SlideOutRight = makeEntering('SlideOutRight');
export const SlideOutLeft = makeEntering('SlideOutLeft');
export const SlideOutDown = makeEntering('SlideOutDown');
export const SlideOutUp = makeEntering('SlideOutUp');
export const ZoomIn = makeEntering('ZoomIn');
export const ZoomOut = makeEntering('ZoomOut');
export const LinearTransition = makeEntering('LinearTransition');
export const Layout = makeEntering('Layout');

export function createAnimatedComponent<T>(Component: T): T {
  return Component;
}

/** Drop Reanimated layout props so RN-web View does not get invalid DOM attrs / layout stalls. */
function stripLayoutProps<P extends Record<string, unknown>>(Component: React.ComponentType<P>) {
  return React.forwardRef<unknown, P>(function AnimatedHost(props, ref) {
    const {
      entering: _entering,
      exiting: _exiting,
      layout: _layout,
      pointerEvents,
      style,
      ...rest
    } = props as P & {
      entering?: unknown;
      exiting?: unknown;
      layout?: unknown;
      pointerEvents?: unknown;
      style?: unknown;
    };
    // RN-web: `props.pointerEvents` is deprecated — fold into style.
    const merged_style =
      pointerEvents != null ? [style, { pointerEvents }] : style;
    return <Component ref={ref} {...(rest as P)} style={merged_style as P['style']} />;
  });
}

const Animated = {
  View: stripLayoutProps(View),
  Text: stripLayoutProps(Text),
  Image: stripLayoutProps(Image),
  ScrollView: stripLayoutProps(ScrollView),
  FlatList: stripLayoutProps(FlatList),
  createAnimatedComponent,
};

export default Animated;
